/**
 * Admin ReviewsPage
 *
 * Moderate guest reviews: hide inappropriate content or delete it. Hidden
 * reviews stop showing on the public listing pages.
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { AdminLayout } from '../layout';
import { useAdminReviews, ReviewFilter } from './hooks/useAdminReviews';

const formatDate = (d: Date): string =>
  d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const TABS: { key: ReviewFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'visible', label: 'Visible' },
  { key: 'hidden', label: 'Hidden' },
];

export const ReviewsPage: React.FC = () => {
  const [filter, setFilter] = useState<ReviewFilter>('all');
  const { reviews, isLoading, setHidden, remove } = useAdminReviews(filter);

  const handleHide = async (id: string, hidden: boolean) => {
    try {
      await setHidden(id, hidden);
      toast.success(hidden ? 'Review hidden' : 'Review restored');
    } catch {
      toast.error('Action failed');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this review permanently?')) return;
    try {
      await remove(id);
      toast.success('Review deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reviews</h1>
          <p className="text-sm text-gray-500 mt-1">
            Moderate guest reviews. Hidden reviews are removed from public listing pages.
          </p>
        </div>

        <div className="flex gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === t.key
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading reviews...</div>
          ) : reviews.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No {filter} reviews</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {reviews.map((r) => (
                <li key={r.id} className={`p-4 ${r.hidden ? 'bg-gray-50' : ''}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-amber-500 text-sm">
                          {'★'.repeat(Math.round(r.rating))}
                          <span className="text-gray-300">
                            {'★'.repeat(5 - Math.round(r.rating))}
                          </span>
                        </span>
                        <span className="text-sm font-medium text-gray-900">{r.guestName}</span>
                        {r.hidden && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-600">
                            Hidden
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-700">{r.content}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {r.listingTitle} · {formatDate(r.createdAt)}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <button
                        onClick={() => handleHide(r.id, !r.hidden)}
                        className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                      >
                        {r.hidden ? 'Restore' : 'Hide'}
                      </button>
                      <button
                        onClick={() => handleDelete(r.id)}
                        className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default ReviewsPage;
